<template lang="pug">
  div(class="block-integration-connect")
    h1(class="title-32px fw-700 mb-3") {{ title }}
    div(class="block-integration-connect__instruction")
      h5(class="text-14px dark fw-300 mb-2") {{ $t('Where to find integration token?') }}
      p(class="text-12px fw-300 mb-8") {{ subtitle }}
      p(
        v-if="isPeopleForce"
        class="text-12px fw-300 mb-3"
      ) ⚠️ {{ $t('Only users with the Administrator role can create API keys') }}
      a(
        class="block-integration-connect__instruction-link text-12px fw-300"
        :href="instructionLink"
        target="_blank"
      ) {{ $t('Detailed instruction ↗︎') }}
    v-form(@submit.prevent="submit")
      ValidationObserver(
        ref="form"
        slim
      )
        ValidationProvider(
          :name="wrapQuotesProviderName($t('Token'))"
          vid="token"
          v-slot="{ errors }"
          rules="required"
        )
          v-text-field(
            v-model="token"
            filled
            :error-messages="errors"
            :label="inputLabel"
          )
        template(v-if="showPeopleForceFields")
          ValidationProvider(
            v-slot="{ errors }"
            vid="employees"
            :name="wrapQuotesProviderName($t('Employees'))"
            mode="lazy"
          )
            v-autocomplete(
              v-model="selectedEmployee"
              :label="$t('Select employee')"
              :items="peopleForce.employees"
              item-value="id"
              item-text="name"
              filled
              clearable
              hide-no-data
              :error-messages="errors"
            )
          ValidationProvider(
            :name="wrapQuotesProviderName($t('Folders'))"
            rules="required"
            v-slot="{ errors }"
            vid="folders"
          )
            v-select(
              v-model="selectedFolders"
              multiple
              :items="peopleForce.folders"
              item-text="name"
              item-value="id"
              filled
              :error-messages="errors"
              :label="$t('Select folders')"
            )
        div(class="block-integration-connect__actions")
          v-btn(
            height="48"
            @click="closeModal"
          ) {{ $t('Cancel') }}
          v-btn(
            class="main-button"
            type="submit"
            :loading="loading"
          ) {{ $t('Connect') }}
</template>

<script>
import validate from '~/mixins/validation/validate'
import successSnackbar from '~/modules/snackbar/mixins/successSnackbar'

export default {
  name: 'BlockIntegrationConnect',
  mixins: [validate, successSnackbar],
  props: {
    closeModal: {
      type: Function,
      default: () => {}
    },
    type: {
      type: String,
      default: null,
      validator: value => {
        return ['vchasno', 'peopleforce'].includes(value)
      }
    },
    organizationId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      token: null,
      loading: false,
      peopleForce: {
        folders: [],
        employees: []
      },
      selectedFolders: [],
      selectedEmployee: null
    }
  },
  computed: {
    isPeopleForce() {
      return this.type === 'peopleforce'
    },
    isVchasno() {
      return this.type === 'vchasno'
    },
    title() {
      return this.isPeopleForce ? this.$t('Connect PeopleForce') : this.$t('Connect Vchasno')
    },
    subtitle() {
      return this.isPeopleForce
        ? this.$t(
            'Go to PeopleForce → Settings → Security → API keys (at the bottom of the page) → click Generate'
          )
        : this.$t('Go to your personal account on Vchasno and copy the access token')
    },
    inputLabel() {
      return this.isPeopleForce
        ? this.$t('Enter token PeopleForce')
        : this.$t('Enter token Vchasno')
    },
    instructionLink() {
      return this.isPeopleForce
        ? 'https://help.peopleforce.io/uk/articles/8718269-%D1%96%D0%BD%D1%82%D0%B5%D0%B3%D1%80%D0%B0%D1%86%D1%96%D1%97-%D1%87%D0%B5%D1%80%D0%B5%D0%B7-api'
        : 'https://help.vchasno.com.ua/token/'
    },
    showPeopleForceFields() {
      return (
        this.isPeopleForce && this.peopleForce.employees.length && this.peopleForce.folders.length
      )
    }
  },
  watch: {
    token: {
      async handler(value) {
        if (this.isPeopleForce && value) {
          await this.getPeopleForceData()
        }
      }
    }
  },
  methods: {
    async getPeopleForceData() {
      try {
        this.loading = true
        const { folders, employees } = await this.$store.dispatch('peopleforce/connection', {
          accessToken: this.token
        })
        this.peopleForce.folders = folders
        this.peopleForce.employees = employees
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    },
    async submitPeopleForce() {
      const payload = {
        active: true,
        accessToken: this.token,
        organization: '/api/v1/organizations/' + this.organizationId,
        folderIds: this.selectedFolders,
        managerId: this.selectedEmployee
      }
      await this.$store.dispatch('peopleforce/create', { payload })
      await this.$store.dispatch('peopleforce/getAll', {})
    },
    async submitVchasno() {
      await this.$store.dispatch('integrations/create', {
        payload: {
          organization: '/api/v1/organizations/' + this.organizationId,
          active: true,
          provider: 'vchasno',
          accessToken: this.token
        }
      })
      await this.$store.dispatch('integrations/getAll', {
        config: {
          headers: {
            'X-Organization': this.$route.params.id
          }
        }
      })
    },
    async submit() {
      const valid = await this.$refs.form.validate()
      if (!valid) {
        return false
      }
      try {
        this.loading = true
        if (this.isVchasno) {
          await this.submitVchasno()
        } else if (this.isPeopleForce) {
          await this.submitPeopleForce()
        }
        this.closeModal()
        await this.successSnackbar.open({
          text: this.$t('Integration has been successfully connected')
        })
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.block-integration-connect {
  &__instruction {
    width: 100%;
    padding: 16px;

    margin-bottom: 32px;

    border-radius: 12px;
    background: $background-gray;
  }
  &__instruction-link {
    color: $primary;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
  }
  &__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
}
</style>
